<template>
  <div class="vdb-page-surface page-graph-paper">
    <!-- HERO -->
    <section class="hero-section">
      <div class="hero-grid">
        <!-- Left: copy -->
        <div v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
          <div class="vdb-eyebrow" style="margin-bottom: 24px">
            <span class="dot"></span> Reference library &middot; updated daily
          </div>
          <h1 class="vdb-display" style="margin: 0 0 24px">
            The only short-form video reference <em>you'll ever need.</em>
          </h1>
          <p style="font-size: 19px; line-height: 1.5; color: var(--ink-400); margin: 0 0 32px; max-width: 520px">
            A hand-curated library of high-performing Instagram and TikTok videos, studied, tagged, and broken down by format, concept, and hook so you can stop guessing what to make.
          </p>
          <div class="hero-cta-row">
            <router-link to="/videos" class="vdb-btn-pill" humblytics="home-hero-browse-cta">
              Browse the library
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
            </router-link>
          </div>
        </div>

        <!-- Right: collage of featured video thumbnails -->
        <div class="hero-collage">
          <div
            v-for="i in 5"
            :key="'skel-'+i"
            class="hero-skel"
            :style="collageStyle(i - 1)"
          ></div>
          <template v-if="featuredVideos.length">
            <router-link
              v-for="(video, i) in featuredVideos.slice(0, 5)"
              :key="video.id"
              :to="getFeaturedVideoLink(video)"
              class="hero-collage-card"
              :class="'hero-collage-card--' + i"
              :style="collageStyle(i)"
              :aria-label="`Open video: ${video.simple_description || 'Featured video'}`"
              humblytics="home-hero-featured-video"
            >
              <div class="hero-collage-card-frame">
                <img
                  v-if="video.thumbnail"
                  :src="video.thumbnail"
                  :alt="video.simple_description || ''"
                  loading="lazy"
                />
                <div class="hero-collage-overlay"></div>
                <div v-if="video.views || video.view_count" class="hero-thumb-views">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                  {{ formatViews(video.views || video.view_count) }}
                </div>
              </div>
            </router-link>
          </template>
          <!-- Floating glass testimonial -->
          <div v-if="featuredVideos.length" class="hero-glass-quote">
            <div class="vdb-italic" style="font-size: 14px; line-height: 1.4; color: var(--ink-700)">
              "I don't know what I did before I had this."
            </div>
            <div style="margin-top: 8px; font-size: 11px; color: var(--ink-400); font-weight: 500">- Everybody</div>
          </div>
        </div>
      </div>
    </section>

    <!-- MCP CONNECTION -->
    <section class="home-mcp-section">
      <div class="home-mcp-inner">
        <div class="home-mcp-copy" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
          <div class="vdb-eyebrow"><span class="dot"></span> Beta &middot; VideoDatabase MCP</div>
          <h2 class="vdb-display-md">Put the whole library <em>inside your AI workspace</em></h2>
          <p>
            Search proven videos, submit missing links for analysis, pull Video DNA, and generate hooks and scripts from Codex, Claude, Cursor, Grok, ChatGPT, and other MCP clients.
          </p>
          <div class="home-mcp-actions">
            <router-link to="/mcp/install" class="vdb-btn-pill" humblytics="home-mcp-install">
              VideoDatabase MCP Beta
              <span aria-hidden="true">&rarr;</span>
            </router-link>
            <span class="home-mcp-note">Beta &middot; Remote HTTPS &middot; scoped access &middot; 19 tools</span>
          </div>
        </div>

        <div class="home-mcp-demo" v-reveal="{ type: 'vdb-reveal-soft', delay: 120 }" aria-label="Example VideoDatabase MCP Beta tool sequence">
          <div class="home-mcp-demo-head">
            <span><i></i> VIDEODATABASE MCP BETA</span>
            <em>CONNECTED</em>
          </div>
          <div class="home-mcp-request">“Find breakout founder-story videos and show me what they have in common.”</div>
          <div class="home-mcp-tools">
            <div><span>01</span><strong>search_videos</strong><small>12 results</small></div>
            <div><span>02</span><strong>get_video_dna</strong><small>pattern decoded</small></div>
            <div><span>03</span><strong>ask_strategy_bot</strong><small>analysis ready</small></div>
            <div class="home-mcp-response">
              <div class="home-mcp-response-head">
                <span>04</span><strong>response</strong><small>3 content blocks</small>
              </div>
              <div class="home-mcp-response-grid">
                <div v-for="(video, i) in mcpResponseVideos" :key="`mcp-response-${video.id || i}`" class="home-mcp-content-block">
                  <img v-if="video.thumbnail" :src="video.thumbnail" :alt="video.simple_description || ''" loading="lazy" />
                  <i v-else :style="mcpResponseThumbStyle(i)"></i>
                  <span>{{ video.author_username ? `@${video.author_username}` : 'Founder story' }}</span>
                </div>
                <div v-for="i in Math.max(0, 3 - mcpResponseVideos.length)" :key="`mcp-response-placeholder-${i}`" class="home-mcp-content-block">
                  <i :style="mcpResponseThumbStyle(i + mcpResponseVideos.length)"></i>
                  <span>Founder story</span>
                </div>
              </div>
              <p>Shared pattern: personal stakes, a specific turning point, and proof before the pitch.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- THIS WEEK'S OUTLIERS -->
    <section ref="bangersSection" style="padding: 56px var(--vdb-pad-x)">
      <div style="max-width: var(--vdb-max-w); margin: 0 auto">
        <div class="vdb-section-head" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
          <div>
            <div class="vdb-eyebrow" style="margin-bottom: 8px">Recent high performers</div>
            <h2><em>This week's</em> bangers</h2>
          </div>
          <div class="meta">
            <span>{{ bangerVideos.length }} of {{ weeklyVideoCount || '184' }}</span>
            <router-link to="/videos?sort=outlier" humblytics="home-outliers-view-all">View all &rarr;</router-link>
          </div>
        </div>
        <div
          v-if="bangerVideos.length"
          ref="bangersScrollerWrap"
          class="bangers-scroller-wrap"
          :class="{ 'is-visible': bangersSectionVisible }"
          @mouseenter="bangersScrollPaused = true"
          @mouseleave="bangersScrollPaused = false"
          @touchstart.passive="bangersScrollPaused = true"
          @touchend.passive="bangersScrollPaused = false"
        >
          <div
            ref="bangersScroller"
            class="bangers-scroller"
            @mousedown="onBangersDragStart"
            @mousemove="onBangersDragMove"
            @mouseup="onBangersDragEnd"
            @mouseleave="onBangersDragEnd"
          >
            <div
              v-for="(video, i) in bangerVideosLoop"
              :key="'banger-' + i + '-' + video.id"
              class="bangers-scroller-item"
              :style="bangerItemStyle(i)"
            >
              <VdbVideoCard :video="video" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <hr class="vdb-dash" style="margin: 0 auto; max-width: var(--vdb-max-w)"/>

    <!-- TWO AUDIENCES -->
    <section style="padding: 64px var(--vdb-pad-x); background: var(--cream-100); border-top: 1px dashed var(--cream-500); border-bottom: 1px dashed var(--cream-500)">
      <div style="max-width: var(--vdb-max-w); margin: 0 auto">
        <!-- Section header -->
        <div class="two-aud-header">
          <div>
            <div class="vdb-eyebrow" style="margin-bottom: 16px">
              <span class="dot"></span> Two audiences &middot; one library
            </div>
            <h2 class="vdb-display-md" style="margin: 0 0 16px">
              Whether you post <em>as the brand</em> — or <em>as yourself</em>.
            </h2>
            <p style="font-size: 14.5px; line-height: 1.55; color: var(--ink-400); max-width: 620px; margin: 0; text-wrap: pretty">
              There are videos for personal brands and experts, videos ideal for brands or local businesses, and videos that work for both. We catalog and organize for both so you can dive into what's right for your use case.
            </p>
          </div>
          <div class="two-aud-header-meta">
            <router-link to="/account-types" humblytics="home-account-types-link" style="font-size: 13px; color: var(--ink-500); border-bottom: 1px solid currentColor; text-decoration: none">View account types &rarr;</router-link>
          </div>
        </div>

        <!-- Two columns -->
        <div class="two-aud-grid">
          <!-- Brand column -->
          <div class="two-aud-col" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading', delay: 80 }">
            <div style="display: flex; align-items: baseline; gap: 12px; margin-bottom: 16px">
              <span class="vdb-italic" style="font-size: 38px; color: var(--warm-600, #c25a30)">01</span>
              <span style="font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-700)">For brands &amp; businesses</span>
            </div>
            <h3 class="vdb-display-md" style="margin: 0 0 20px; font-size: 32px">Built to <em>sell</em>.</h3>

            <!-- Fanned thumbnails -->
            <div class="two-aud-fan-container vdb-thumb-stack-motion vdb-collage-sequence" v-reveal="{ type: 'vdb-collage-trigger', delay: 80 }">
              <div
                v-for="(thumb, i) in audienceData.brand?.thumbnails || []"
                :key="'brand-' + i"
                class="two-aud-fan-card"
                :style="fanCardStyle(i)"
              >
                <img :src="thumb" alt="" loading="lazy" />
              </div>
              <div v-if="!audienceData.brand?.thumbnails?.length" v-for="i in 3" :key="'brand-skel-' + i" class="two-aud-fan-card two-aud-fan-skel" :style="fanCardStyle(i - 1)"></div>
            </div>

            <p style="font-size: 14.5px; line-height: 1.55; color: var(--ink-400); max-width: 480px; margin: 0 0 20px; text-wrap: pretty">
              Brand-side reels and TikToks where the company is the protagonist — product demos, founder stories, behind-the-scenes that move pipeline, not just impressions.
            </p>
            <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 24px">
              <router-link to="/concepts/product-showcase" class="vdb-chip" humblytics="home-brand-concept-chip" style="text-decoration: none; color: inherit">Product Showcase</router-link>
              <router-link to="/concepts/behind-the-scenes" class="vdb-chip" humblytics="home-brand-concept-chip" style="text-decoration: none; color: inherit">Behind The Scenes</router-link>
              <router-link to="/concepts/skit" class="vdb-chip" humblytics="home-brand-concept-chip" style="text-decoration: none; color: inherit">Skit</router-link>
              <router-link to="/concepts/lifestyle-showcase" class="vdb-chip" humblytics="home-brand-concept-chip" style="text-decoration: none; color: inherit">Lifestyle Showcase</router-link>
              <router-link to="/concepts/social-show" class="vdb-chip" humblytics="home-brand-concept-chip" style="text-decoration: none; color: inherit">Social Show</router-link>
            </div>
            <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap">
              <router-link to="/account-types/brand" class="vdb-btn-pill" humblytics="home-browse-brand">
                Browse brand library
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
              </router-link>
            </div>
          </div>

          <!-- Vertical divider -->
          <div class="two-aud-divider"></div>

          <!-- Creator column -->
          <div class="two-aud-col" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading', delay: 160 }">
            <div style="display: flex; align-items: baseline; gap: 12px; margin-bottom: 16px">
              <span class="vdb-italic" style="font-size: 38px; color: var(--blue-600, #2a6fdb)">02</span>
              <span style="font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-700)">For personal brands &amp; creators</span>
            </div>
            <h3 class="vdb-display-md" style="margin: 0 0 20px; font-size: 32px">Built to <em>be known</em>.</h3>

            <!-- Fanned thumbnails -->
            <div class="two-aud-fan-container vdb-thumb-stack-motion vdb-collage-sequence" v-reveal="{ type: 'vdb-collage-trigger', delay: 140 }">
              <div
                v-for="(thumb, i) in audienceData.creator?.thumbnails || []"
                :key="'creator-' + i"
                class="two-aud-fan-card"
                :style="fanCardStyle(i)"
              >
                <img :src="thumb" alt="" loading="lazy" />
              </div>
              <div v-if="!audienceData.creator?.thumbnails?.length" v-for="i in 3" :key="'creator-skel-' + i" class="two-aud-fan-card two-aud-fan-skel" :style="fanCardStyle(i - 1)"></div>
            </div>

            <p style="font-size: 14.5px; line-height: 1.55; color: var(--ink-400); max-width: 480px; margin: 0 0 20px; text-wrap: pretty">
              Creator-side videos where a single person is the magnet — talking head expertise, day-in-the-life, expert ratings, and the visual hooks that get a face remembered.
            </p>
            <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 24px">
              <router-link to="/concepts/breakdown" class="vdb-chip" humblytics="home-creator-concept-chip" style="text-decoration: none; color: inherit">Breakdown</router-link>
              <router-link to="/concepts/tutorial-how-to" class="vdb-chip" humblytics="home-creator-concept-chip" style="text-decoration: none; color: inherit">Tutorial / How To</router-link>
              <router-link to="/concepts/hot-take" class="vdb-chip" humblytics="home-creator-concept-chip" style="text-decoration: none; color: inherit">Hot Take</router-link>
              <router-link to="/concepts/relatable-skit" class="vdb-chip" humblytics="home-creator-concept-chip" style="text-decoration: none; color: inherit">Relatable Skit</router-link>
              <router-link to="/concepts/rapid-fire-listicle" class="vdb-chip" humblytics="home-creator-concept-chip" style="text-decoration: none; color: inherit">Rapid Fire Listicle</router-link>
            </div>
            <div style="display: flex; align-items: center; gap: 16px; flex-wrap: wrap">
              <router-link to="/account-types/personal-brand" class="vdb-btn-pill" humblytics="home-browse-creator">
                Browse personal brand library
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- HOOK ANALYSIS + TEMPLATES -->
    <section class="home-hooks-section">
      <div style="max-width: var(--vdb-max-w); margin: 0 auto">
        <div class="home-hooks-layout">
          <div class="home-hooks-copy" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
            <div class="vdb-eyebrow" style="margin-bottom: 8px">Hook analysis + templates</div>
            <h2 class="vdb-display-md" style="margin: 0 0 16px">Every hook, <em>decoded.</em></h2>
            <p style="margin: 0 0 28px; font-size: 15px; color: var(--ink-400); max-width: 460px; line-height: 1.6">
              We break down every video's opening into three layers: the on-screen text, the spoken line and the visual framing. Then we turn each one into a reusable template. On Pro, Creator Panel takes any video in the library and writes hook ideas, video ideas or a script in your saved voice.
            </p>
            <router-link to="/hooks" class="vdb-btn-pill home-hooks-cta-desktop" humblytics="home-hooks-cta">
              Explore hooks
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
            </router-link>
          </div>
          <div class="home-hooks-graphic">
            <div class="home-hooks-stack vdb-collage-sequence" v-reveal="{ type: 'vdb-collage-trigger', delay: 180 }">
              <div class="home-hook-float home-hook-float-1">
                <div class="home-hook-pills">
                  <span class="hf-pill" style="background: #E2EAF7; color: #1C548D">Title</span>
                  <span class="hf-pill" style="background: #FBE5D3; color: #D85600">Verbal</span>
                </div>
                <p class="home-hook-text">"POV: you <span class="hf-var">[action]</span> and it actually <span class="hf-var">[result]</span>"</p>
              </div>
              <div class="home-hook-float home-hook-float-2">
                <div class="home-hook-pills">
                  <span class="hf-pill" style="background: #FBE5D3; color: #D85600">Verbal</span>
                </div>
                <p class="home-hook-text">"Nobody talks about <span class="hf-var">[hidden truth]</span> in <span class="hf-var">[industry]</span>"</p>
              </div>
              <div class="home-hook-float home-hook-float-3">
                <div class="home-hook-pills">
                  <span class="hf-pill" style="background: #E8F4DB; color: #2d6b22">Visual</span>
                  <span class="hf-pill" style="background: #E2EAF7; color: #1C548D">Title</span>
                </div>
                <p class="home-hook-text">"<span class="hf-var">[Number]</span> things I wish I knew about <span class="hf-var">[topic]</span>"</p>
              </div>
            </div>
          </div>
          <router-link to="/hooks" class="vdb-btn-pill home-hooks-cta-mobile" humblytics="home-hooks-cta-mobile">
            Explore hooks
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
          </router-link>
        </div>
      </div>
    </section>

    <hr class="vdb-dash" style="margin: 0 auto; max-width: var(--vdb-max-w)"/>

    <!-- BROWSE BY FORMAT -->
    <section style="padding: 56px var(--vdb-pad-x); background: var(--bg-section-gradient-flipped)">
      <div style="max-width: var(--vdb-max-w); margin: 0 auto">
      <div class="vdb-section-head" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
        <div>
          <div class="vdb-eyebrow" style="margin-bottom: 8px">How a video is made</div>
          <h2>Browse by <em>format</em></h2>
          <p style="margin-top: 12px; margin-bottom: 0; font-size: 15px; color: var(--ink-400); max-width: 540px">
            The structural template a video uses. The same idea in a 10-shot vs a one-shot is a different video.
          </p>
        </div>
        <div class="meta">
          <router-link to="/formats" humblytics="home-formats-view-all">All formats &rarr;</router-link>
        </div>
      </div>
      <div class="home-format-grid">
        <router-link
          v-for="(f, i) in topFormats"
          :key="f.format_slug || f.slug || f.id || i"
          :to="'/formats/' + (f.format_slug || f.slug)"
          class="vdb-tile"
          v-reveal="{ type: 'vdb-reveal-soft', delay: Math.min(220, i * 35) }"
          humblytics="home-format-tile"
        >
          <div class="vdb-tile-thumb-stack" :class="`vdb-tile-thumb-stack--palette-${i % 6}`">
            <div class="t"><img v-if="f.thumbnails?.[0]" :src="f.thumbnails[0]" alt="" loading="lazy" /></div>
            <div class="t"><img v-if="f.thumbnails?.[1]" :src="f.thumbnails[1]" alt="" loading="lazy" /></div>
            <div class="t"><img v-if="f.thumbnails?.[2]" :src="f.thumbnails[2]" alt="" loading="lazy" /></div>
          </div>
          <div class="vdb-tile-body">
            <div class="vdb-tile-title">{{ f.name }}</div>
            <div class="vdb-tile-meta"><em>{{ f.count }}</em> videos</div>
          </div>
        </router-link>
      </div>
      </div>
    </section>

    <hr class="vdb-dash" style="margin: 0 auto; max-width: var(--vdb-max-w)"/>

    <!-- BROWSE BY CONCEPT -->
    <section style="padding: 56px var(--vdb-pad-x)" v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
      <div style="max-width: var(--vdb-max-w); margin: 0 auto">
      <div class="vdb-section-head">
        <div>
          <div class="vdb-eyebrow" style="margin-bottom: 8px">How a video is packaged</div>
          <h2>Browse by <em>concept</em></h2>
        </div>
        <div class="meta">
          <router-link to="/concepts" humblytics="home-concepts-view-all">All concepts &rarr;</router-link>
        </div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px">
        <div
          v-for="(c, i) in topConcepts"
          :key="c.concept_slug || c.slug || c.id || i"
          class="tile-with-tab"
        >
          <div class="tile-tab">C.{{ String(i + 1).padStart(2, '0') }}</div>
          <router-link
            :to="'/concepts/' + (c.concept_slug || c.slug)"
            class="concept-tile"
            humblytics="home-concept-tile"
          >
            <div class="concept-tile-name">{{ c.name }}</div>
            <div class="concept-tile-count"><em>{{ c.count || 0 }}</em> videos</div>
          </router-link>
        </div>
      </div>
      </div>
    </section>

    <section v-if="latestArticle" class="home-editorial">
      <div class="home-editorial-inner">
        <router-link
          :to="`/blog/${latestArticle.slug}`"
          class="home-editorial-feature"
          :aria-label="`Read ${latestArticle.title}`"
          humblytics="home-editorial-latest"
        >
          <img
            v-if="latestArticle.hero_image_url"
            :src="latestArticle.hero_image_url"
            :alt="latestArticle.hero_image_alt || ''"
            width="1280"
            height="720"
            loading="lazy"
          >
          <div v-else class="home-editorial-fallback">VDB EDITORIAL</div>
          <div class="home-editorial-overlay" aria-hidden="true"></div>
          <div class="home-editorial-content">
            <p class="home-editorial-meta">
              {{ editorialTypeLabel(latestArticle.article_type) }}
              <span aria-hidden="true">/</span>
              <time :datetime="latestArticle.published_at">{{ formatEditorialDate(latestArticle.published_at) }}</time>
            </p>
            <h2>{{ latestArticle.title }}</h2>
            <p class="home-editorial-dek">{{ latestArticle.dek }}</p>
            <div class="home-editorial-footer">
              <span>By {{ latestArticle.author?.name || humanizeEditorial(latestArticle.author_slug) || 'VideoDatabase Editorial' }}</span>
              <strong>Read the report <span aria-hidden="true">&rarr;</span></strong>
            </div>
          </div>
        </router-link>
      </div>
    </section>

    <!-- DARK BAND: CUT30 SIBLING -->
    <section class="vdb-dark-band">
      <div style="display: grid; grid-template-columns: 1.3fr 1fr; gap: 64px; align-items: center">
        <div v-reveal="{ type: 'vdb-reveal-up vdb-type-motion-heading' }">
          <div class="vdb-eyebrow" style="color: rgba(255,255,255,0.6); margin-bottom: 20px">
            <span class="dot" style="background: rgba(255,255,255,0.7)"></span> Cut30
          </div>
          <h2 class="vdb-display-md" style="color: white; margin: 0 0 18px">
            Study the patterns. Then <em>press record</em>.
          </h2>
          <p style="font-size: 16px; line-height: 1.55; color: rgba(255,255,255,0.75); max-width: 480px; margin: 0 0 28px">
            Video Database is a reference library for organic content, and Cut30 is our 30-day bootcamp to get you making amazing content for your personal brand or business.
          </p>
          <div style="display: flex; gap: 12px; flex-wrap: wrap">
            <a href="https://cut30.co" target="_blank" rel="noopener" humblytics="ext-site-cut30" class="vdb-btn-pill light">Look at Cut30 &rarr;</a>
            <router-link to="/videos" humblytics="home-stay-library" class="vdb-btn-pill ghost" style="border-color: rgba(255,255,255,0.25); color: white">Stay in the library</router-link>
          </div>
        </div>
        <div class="dark-band-thumbs vdb-thumb-stack-motion vdb-collage-sequence" v-reveal="{ type: 'vdb-collage-trigger', delay: 100 }">
          <div v-for="(video, i) in cut30Videos.slice(0, 6)" :key="'db-' + video.id" class="dark-band-thumb" :style="darkBandThumbStyle(i)">
            <img v-if="video.thumbnail" :src="video.thumbnail" :alt="''" loading="lazy" />
            <div v-if="video.views || video.view_count" class="dark-band-views">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
              {{ formatViews(video.views || video.view_count) }}
            </div>
          </div>
          <div class="dark-band-badge-float">
            <Cut30AlumBadge />
          </div>
        </div>
      </div>
    </section>

    <!-- PRICING CTA -->
    <PricingCTAHero />
  </div>
</template>

<script>
import VdbVideoCard from '@/components/vdb/VdbVideoCard.vue';
import DailyUpdateTicker from '@/components/DailyUpdateTicker.vue';
import Cut30AlumBadge from '@/components/Cut30AlumBadge.vue';
import PricingCTAHero from '@/components/PricingCTAHero.vue';
import api, { videoApi } from '@/services/api.js';
import {
  THUMBNAIL_WIDTHS,
  normalizeVideoCollection,
  buildVideoUrl,
  optimizeThumbnailUrl
} from '@/utils/videoUrl.js';

const THUMB_GRADIENTS = [
  'linear-gradient(135deg, #d97757 0%, #8a3f2a 100%)',
  'linear-gradient(135deg, #2a6fdb 0%, #0c1824 100%)',
  'linear-gradient(135deg, #1f8a5b 0%, #0c1824 100%)',
  'linear-gradient(135deg, #c9a961 0%, #6e5b2b 100%)',
  'linear-gradient(135deg, #a64bdb 0%, #2a1052 100%)',
  'linear-gradient(135deg, #db4b85 0%, #5a1f3a 100%)',
  'linear-gradient(135deg, #6e8b9a 0%, #1f3a52 100%)',
  'linear-gradient(135deg, #e8c469 0%, #b87a1f 100%)',
  'linear-gradient(135deg, #5f9b6d 0%, #1f3a2a 100%)',
  'linear-gradient(135deg, #c97f5a 0%, #6e3a1f 100%)',
  'linear-gradient(135deg, #4b8bdb 0%, #1f3a85 100%)',
  'linear-gradient(135deg, #db6b4b 0%, #5a1f0f 100%)',
];

export default {
  name: 'PublicHome',
  components: { VdbVideoCard, DailyUpdateTicker, Cut30AlumBadge, PricingCTAHero },
  data() {
    return {
      featuredVideos: [],
      bangerVideos: [],
      loadingFeaturedVideos: false,
      totalVideos: 47312,
      topFormats: [],
      topConcepts: [],
      weeklyVideoCount: null,
      cut30Videos: [],
      audienceData: { brand: { count: 0, thumbnails: [] }, creator: { count: 0, thumbnails: [] }, total: 0 },
      latestArticle: null,
      bangersScrollPaused: false,
      bangersScrollRAF: null,
      bangersScrollObserver: null,
      bangersSectionVisible: false,
      bangersDragging: false,
      bangersDragPending: false,
      bangersDragStartX: 0,
      bangersDragScrollLeft: 0,
    };
  },
  computed: {
    weeklyVideoMessage() {
      if (this.weeklyVideoCount === null) return 'Thousands of videos in the database';
      return `${this.weeklyVideoCount} new videos added to the database this week`;
    },
    bangerVideosLoop() {
      if (!this.bangerVideos.length) return [];
      return [...this.bangerVideos, ...this.bangerVideos, ...this.bangerVideos];
    },
    mcpResponseVideos() {
      const videos = this.bangerVideos.length ? this.bangerVideos : this.featuredVideos;
      return videos.slice(0, 3);
    },
  },
  async mounted() {
    this.warmupSemanticSearch();
    await this.loadFeaturedVideos();
    this.fetchWeeklyVideoCount();
    this.loadTaxonomyData();
    this.loadCut30Videos();
    this.loadAudienceCounts();
    this.loadLatestArticle();
  },
  beforeUnmount() {
    this.teardownBangersScrollObserver();
  },
  methods: {
    editorialTypeLabel(type) {
      return type === 'guide' ? 'Field guide' : type === 'brand_roundup' ? 'Brand index' : type === 'video_roundup' ? 'Video index' : 'Creator index';
    },
    formatEditorialDate(value) {
      if (!value) return '';
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    humanizeEditorial(value) {
      return String(value || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    },
    async loadLatestArticle() {
      try { this.latestArticle = (await videoApi.get('/blog?limit=1')).data.data?.[0] || null; } catch { this.latestArticle = null; }
    },
    warmupSemanticSearch() {
      videoApi.getPublicVideos({ limit: 1, page: 1, search: 'warmup' }).catch(() => {});
    },
    async fetchWeeklyVideoCount() {
      try {
        const response = await api.get('/stats/weekly-videos');
        if (response.data.success) this.weeklyVideoCount = response.data.count;
      } catch {}
    },
    async loadFeaturedVideos() {
      try {
        this.loadingFeaturedVideos = true;
        const response = await videoApi.getPublicVideos({
          outlierScoreMin: 2.0,
          viewsMin: 500000,
          postDateRelative: 'week',
          limit: 20,
          page: 1,
        });
        const videos = response.data?.data || [];
        if (videos.length > 0) {
          const shuffled = [...videos].sort(() => Math.random() - 0.5);
          const normalized = shuffled
            .map(v => normalizeVideoCollection(v, THUMBNAIL_WIDTHS.heroCollage))
            .filter(Boolean);
          // Deduplicate so each thumbnail comes from a unique author
          const seen = new Set();
          const unique = [];
          for (const v of normalized) {
            const author = v.author_username || v.username || v.id;
            if (!seen.has(author)) {
              seen.add(author);
              unique.push(v);
            }
            if (unique.length >= 11) break;
          }
          this.featuredVideos = unique.slice(0, 5);
          this.bangerVideos = unique.slice(5, 11);
          this.$nextTick(() => this.setupBangersScrollObserver());
        }
      } catch (e) {
        console.error('Failed to load featured videos:', e);
      } finally {
        this.loadingFeaturedVideos = false;
      }
    },
    getFeaturedVideoLink(video) {
      return buildVideoUrl(video) || `/video/${video?.id ?? ''}`;
    },
    collageStyle(i) {
      const positions = [
        { top: '0', left: '0', width: '180px', rotate: '-6deg', hover: 'translate(-10px, 0) rotate(-2deg) scale(1.018)', active: 'translate(-12px, -3px) rotate(-2deg) scale(1.03)', enterX: '-38px', enterY: '-24px' },
        { top: '20px', left: '170px', width: '200px', rotate: '3deg', hover: 'translate(2px, -8px) rotate(-2deg) scale(1.02)', active: 'translate(3px, -12px) rotate(-2deg) scale(1.032)', enterX: '22px', enterY: '-38px' },
        { top: '220px', left: '40px', width: '200px', rotate: '-2deg', hover: 'translate(-5px, 8px) rotate(-2deg) scale(1.016)', active: 'translate(-6px, 5px) rotate(-2deg) scale(1.028)', enterX: '-34px', enterY: '34px' },
        { top: '200px', left: '240px', width: '170px', rotate: '7deg', hover: 'translate(10px, -5px) rotate(2deg) scale(1.018)', active: 'translate(12px, -8px) rotate(2deg) scale(1.03)', enterX: '38px', enterY: '30px' },
        { top: '40px', left: '370px', width: '160px', rotate: '-4deg', hover: 'translate(9px, 1px) rotate(2deg) scale(1.016)', active: 'translate(11px, -2px) rotate(2deg) scale(1.028)', enterX: '44px', enterY: '-18px' },
      ];
      const p = positions[i] || positions[0];
      return {
        position: 'absolute',
        top: p.top,
        left: p.left,
        width: p.width,
        '--home-card-transform': `rotate(${p.rotate})`,
        '--home-card-hover-transform': p.hover,
        '--home-card-active-transform': p.active,
        '--home-card-enter-x': p.enterX,
        '--home-card-enter-y': p.enterY,
        '--home-card-delay': `${120 + i * 85}ms`,
        zIndex: i,
      };
    },
    setupBangersScrollObserver() {
      this.teardownBangersScrollObserver();
      const section = this.$refs.bangersSection;
      if (!section) return;

      if (typeof IntersectionObserver === 'undefined') {
        this.bangersSectionVisible = true;
        this.startBangersScroll();
        return;
      }

      this.bangersScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.bangersSectionVisible = true;
              this.startBangersScroll();
            } else {
              this.stopBangersScroll();
            }
          });
        },
        { rootMargin: '0px 0px 28% 0px', threshold: 0.01 }
      );
      this.bangersScrollObserver.observe(section);
    },
    teardownBangersScrollObserver() {
      if (this.bangersScrollObserver) {
        this.bangersScrollObserver.disconnect();
        this.bangersScrollObserver = null;
      }
      this.stopBangersScroll();
    },
    startBangersScroll() {
      if (this.bangersScrollRAF) return;
      const el = this.$refs.bangersScroller;
      if (!el) return;
      const step = () => {
        if (!this.bangersScrollPaused && el) {
          el.scrollLeft += 0.5;
          if (el.scrollLeft >= el.scrollWidth / 3) {
            el.scrollLeft = 0;
          }
        }
        this.bangersScrollRAF = requestAnimationFrame(step);
      };
      this.bangersScrollRAF = requestAnimationFrame(step);
    },
    bangerItemStyle(i) {
      return {
        '--banger-card-delay': `${Math.min(180, (i % Math.max(this.bangerVideos.length, 1)) * 30)}ms`,
        pointerEvents: this.bangersDragging ? 'none' : undefined,
      };
    },
    stopBangersScroll() {
      if (this.bangersScrollRAF) {
        cancelAnimationFrame(this.bangersScrollRAF);
        this.bangersScrollRAF = null;
      }
    },
    onBangersDragStart(e) {
      const el = this.$refs.bangersScroller;
      if (!el) return;
      this.bangersDragPending = true;
      this.bangersDragging = false;
      this.bangersDragStartX = e.pageX - el.offsetLeft;
      this.bangersDragScrollLeft = el.scrollLeft;
      this.bangersScrollPaused = true;
    },
    onBangersDragMove(e) {
      if (!this.bangersDragPending && !this.bangersDragging) return;
      const el = this.$refs.bangersScroller;
      if (!el) return;
      const x = e.pageX - el.offsetLeft;
      const distance = Math.abs(x - this.bangersDragStartX);
      if (!this.bangersDragging && distance < 5) return;
      this.bangersDragging = true;
      e.preventDefault();
      const walk = (x - this.bangersDragStartX) * 1.5;
      el.scrollLeft = this.bangersDragScrollLeft - walk;
    },
    onBangersDragEnd() {
      this.bangersDragPending = false;
      this.bangersDragging = false;
      this.bangersScrollPaused = false;
    },
    darkBandThumbStyle(i) {
      const rot = (i % 2 ? 3 : -3) + (i - 2);
      return {
        '--dark-thumb-transform': `rotate(${rot}deg)`,
        '--collage-card-delay': `${i * 55}ms`,
        background: THUMB_GRADIENTS[i % THUMB_GRADIENTS.length],
      };
    },
    mcpResponseThumbStyle(i) {
      return { background: THUMB_GRADIENTS[i % THUMB_GRADIENTS.length] };
    },
    async loadTaxonomyData() {
      try {
        const res = await videoApi.getPublicFilters();
        if (res.data?.success) {
          const formats = (res.data.data.formats || [])
            .filter(f => (f.count || f.video_count || 0) > 0)
            .sort((a, b) => (b.count || b.video_count || 0) - (a.count || a.video_count || 0))
            .slice(0, 10)
            .map(f => ({ ...f, slug: f.format_slug || f.slug, count: f.count || f.video_count || 0, thumbnails: [] }));
          this.topFormats = formats;
          this.loadFormatThumbnails();

          const concepts = (res.data.data.concepts || [])
            .filter(c => (c.count || c.video_count || 0) > 0)
            .sort((a, b) => (b.count || b.video_count || 0) - (a.count || a.video_count || 0))
            .slice(0, 10)
            .map(c => ({ ...c, slug: c.concept_slug || c.slug, count: c.count || c.video_count || 0 }));
          this.topConcepts = concepts;
        }
      } catch {}
    },
    async loadFormatThumbnails() {
      const updated = await Promise.all(this.topFormats.map(async (f) => {
        try {
          const res = await videoApi.getFormatTopVideos(f.slug, { limit: 3 });
          const videos = res.data?.data || [];
          const thumbnails = videos
            .map(v => optimizeThumbnailUrl(v.cover_url || v.thumbnail_url || v.thumbnail, THUMBNAIL_WIDTHS.compactCard))
            .filter(Boolean);
          return { ...f, thumbnails };
        } catch {
          return { ...f, thumbnails: [] };
        }
      }));
      this.topFormats = updated;
    },
    async loadCut30Videos() {
      try {
        const response = await api.get('/cut30/top-videos', { params: { limit: 8 } });
        const videos = (response.data?.data || []).map(v => normalizeVideoCollection(v)).filter(Boolean);
        this.cut30Videos = videos.length > 0
          ? [...videos].sort(() => Math.random() - 0.5).slice(0, 6)
          : [];
      } catch { this.cut30Videos = []; }
    },
    formatViews(n) {
      if (!n) return '0';
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      return String(n);
    },
    async loadAudienceCounts() {
      try {
        const res = await api.get('/stats/audience-counts');
        if (res.data?.success) {
          this.audienceData = res.data.data;
        }
      } catch {}
    },
    fanCardStyle(i) {
      const rotations = [-5, 2, 6];
      const w = window.innerWidth;
      const spacing = w <= 560 ? 90 : w <= 880 ? 110 : 130;
      return {
        position: 'absolute',
        top: `${i * 8}px`,
        left: `${i * spacing}px`,
        '--fan-card-transform': `rotate(${rotations[i]}deg)`,
        '--collage-card-delay': `${i * 90}ms`,
        zIndex: i,
      };
    },
    formatNumber(n) {
      if (!n) return '0';
      return n.toLocaleString();
    },
  },
};
</script>

<style scoped>
.home-editorial { padding: 70px var(--vdb-pad-x); border-top: 1px dashed var(--cream-500); background: var(--paper); }
.home-editorial-inner { max-width: var(--vdb-max-w); margin: 0 auto; }
.home-editorial-feature { position: relative; display: block; min-height: 560px; overflow: hidden; border: 1px solid var(--line-200); border-radius: var(--vdb-card-radius); background: var(--cream-200); color: var(--ink-900); text-decoration: none; box-shadow: var(--shadow-card-sm); isolation: isolate; }
.home-editorial-feature > img { position: absolute; z-index: -3; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .55s cubic-bezier(.2,.7,.2,1); }
.home-editorial-feature:hover > img { transform: scale(1.018); }
.home-editorial-fallback { position: absolute; z-index: -3; inset: 0; display: grid; place-items: center; background: var(--ink-800); color: rgba(255,255,255,.55); font-size: 12px; letter-spacing: .12em; }
.home-editorial-overlay { position: absolute; z-index: -2; inset: 0; background: linear-gradient(90deg, rgba(250,247,241,.98) 0%, rgba(250,247,241,.94) 29%, rgba(250,247,241,.62) 48%, rgba(250,247,241,.12) 72%, rgba(250,247,241,.04) 100%); }
.home-editorial-overlay::after { content: ''; position: absolute; inset: 0; background: linear-gradient(0deg, rgba(16,24,29,.08), transparent 35%); }
.home-editorial-content { position: relative; z-index: 1; display: flex; flex-direction: column; min-height: 560px; width: min(58%, 710px); padding: clamp(42px, 5vw, 72px); }
.home-editorial-meta { display: flex; align-items: center; gap: 10px; margin: 0 0 30px; color: var(--ink-600); font-size: 11px; font-weight: 700; letter-spacing: .075em; text-transform: uppercase; }
.home-editorial-meta span { color: var(--cream-600); }
.home-editorial-feature h2 { max-width: 680px; margin: 0 0 24px; color: var(--ink-900); font-size: clamp(42px, 4.7vw, 68px); font-weight: 650; letter-spacing: -.035em; line-height: .98; text-wrap: balance; }
.home-editorial-dek { display: -webkit-box; max-width: 610px; margin: 0; overflow: hidden; color: var(--ink-600); font-size: clamp(17px, 1.5vw, 21px); font-weight: 500; line-height: 1.48; text-wrap: pretty; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.home-editorial-footer { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-top: auto; padding-top: 44px; color: var(--ink-700); font-size: 13px; font-weight: 600; }
.home-editorial-footer strong { display: inline-flex; align-items: center; gap: 8px; padding-bottom: 3px; border-bottom: 1px solid currentColor; font-weight: 650; }
.hero-section { padding: 72px var(--vdb-pad-x) 56px; }
.hero-cta-row { display: flex; gap: 12px; margin-bottom: 40px; flex-wrap: wrap; }
.home-mcp-section {
  padding: 28px var(--vdb-pad-x);
  background: var(--cream-100);
  border-top: 1px dashed var(--cream-500);
  border-bottom: 1px dashed var(--cream-500);
}
.home-mcp-inner {
  max-width: var(--vdb-max-w);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(390px, .82fr);
  gap: 54px;
  align-items: center;
}
.home-mcp-copy h2 { margin: 12px 0 12px; font-size: clamp(34px, 3.6vw, 49px); line-height: 1.04; }
.home-mcp-copy > p { max-width: 650px; margin: 0; color: var(--ink-400); font-size: 15px; line-height: 1.58; }
.home-mcp-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; margin-top: 22px; }
.home-mcp-note { color: var(--ink-400); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; }
.home-mcp-demo { padding: 14px; border-radius: 18px; background: var(--ink-800); color: white; box-shadow: var(--shadow-card-md); transform: rotate(.7deg); }
.home-mcp-demo-head { display: flex; justify-content: space-between; align-items: center; padding: 1px 2px 11px; color: rgba(255,255,255,.58); font-size: 9px; font-weight: 700; letter-spacing: .08em; }
.home-mcp-demo-head span { display: flex; align-items: center; gap: 7px; }
.home-mcp-demo-head i { width: 6px; height: 6px; border-radius: 50%; background: #8fd19e; box-shadow: 0 0 0 3px rgba(143,209,158,.12); }
.home-mcp-demo-head em { color: #8fd19e; font-style: normal; }
.home-mcp-request { padding: 11px 13px; border-radius: 10px; background: white; color: var(--ink-800); font-size: 12px; line-height: 1.42; }
.home-mcp-tools { display: grid; gap: 5px; margin-top: 8px; }
.home-mcp-tools > div { display: grid; grid-template-columns: 22px 1fr auto; gap: 8px; align-items: center; padding: 8px 10px; border: 1px solid rgba(255,255,255,.11); border-radius: 8px; background: rgba(255,255,255,.05); }
.home-mcp-tools span { color: #e7c87c; font-family: var(--font-display-italic); font-style: italic; font-size: 11px; }
.home-mcp-tools strong { font-size: 11px; font-weight: 600; }
.home-mcp-tools small { color: rgba(255,255,255,.46); font-size: 9px; }
.home-mcp-tools > .home-mcp-response { display: block; padding: 9px 10px 10px; background: rgba(255,255,255,.08); }
.home-mcp-response-head { display: grid; grid-template-columns: 22px 1fr auto; gap: 8px; align-items: center; }
.home-mcp-response-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-top: 8px; }
.home-mcp-content-block { position: relative; min-width: 0; aspect-ratio: 1.55 / 1; overflow: hidden; border-radius: 6px; background: rgba(255,255,255,.08); }
.home-mcp-content-block img,
.home-mcp-content-block i { display: block; width: 100%; height: 100%; object-fit: cover; }
.home-mcp-content-block::after { content: ''; position: absolute; inset: 45% 0 0; background: linear-gradient(transparent, rgba(12,24,36,.86)); }
.home-mcp-content-block span { position: absolute; z-index: 1; right: 5px; bottom: 4px; left: 5px; overflow: hidden; color: white; font-family: inherit; font-size: 7px; font-style: normal; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.home-mcp-response > p { margin: 8px 1px 0; color: rgba(255,255,255,.72); font-size: 8px; line-height: 1.35; }
.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 64px;
  align-items: center;
  max-width: var(--vdb-max-w);
  margin: 0 auto;
}
.hero-thumb-views {
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  z-index: 2;
}
.hero-collage {
  position: relative;
  height: 460px;
}
.hero-skel {
  position: absolute;
  aspect-ratio: 9/13;
  border-radius: 14px;
  background: var(--cream-300);
  opacity: 0.26;
  pointer-events: none;
  transform: var(--home-card-transform);
}
.hero-collage-card {
  display: block;
  aspect-ratio: 9/13;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  opacity: 0;
  transform: translate(var(--home-card-enter-x, 0), var(--home-card-enter-y, 0)) scale(.88) var(--home-card-transform);
  animation: homeHeroCardIn 720ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: var(--home-card-delay, 120ms);
  transition:
    transform .25s cubic-bezier(.2, .7, .2, 1),
    box-shadow .25s cubic-bezier(.2, .7, .2, 1);
  will-change: transform;
}
.hero-collage-card-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 14px;
  border: 4px solid white;
  box-shadow: 0 12px 32px rgba(0,0,0,0.22), 0 2px 4px rgba(0,0,0,0.08);
  overflow: hidden;
  background: linear-gradient(160deg, #2a2a2e 0%, #1a1a1e 100%);
  transform: none;
  transition:
    transform .25s cubic-bezier(.2, .7, .2, 1),
    box-shadow .25s cubic-bezier(.2, .7, .2, 1);
  will-change: transform;
}
.hero-collage:hover .hero-collage-card-frame {
  transform: var(--home-card-hover-transform, var(--home-card-transform));
  box-shadow: 0 14px 34px rgba(0,0,0,0.22), 0 3px 8px rgba(0,0,0,0.1);
}
.hero-collage-card:hover .hero-collage-card-frame,
.hero-collage:hover .hero-collage-card:hover .hero-collage-card-frame {
  transform: var(--home-card-active-transform, var(--home-card-hover-transform, var(--home-card-transform)));
  box-shadow: 0 17px 40px rgba(0,0,0,0.24), 0 4px 10px rgba(0,0,0,0.12);
}
.hero-collage-card-frame img {
  width: 100%; height: 100%; object-fit: cover;
}
@keyframes homeHeroCardIn {
  to {
    opacity: 1;
    transform: var(--home-card-transform);
  }
}
.vdb-collage-sequence .two-aud-fan-card,
.vdb-collage-sequence .dark-band-thumb,
.vdb-collage-sequence .home-hook-float {
  opacity: 0;
  will-change: opacity, transform;
}
.vdb-collage-sequence .two-aud-fan-card {
  transform: translateY(14px) scale(.965) var(--fan-card-transform, rotate(0deg));
}
.vdb-collage-sequence .dark-band-thumb {
  transform: translateY(16px) scale(.965) var(--dark-thumb-transform, rotate(0deg));
}
.vdb-collage-sequence .home-hook-float {
  transform: translateY(14px) scale(.965) var(--hook-card-transform, rotate(0deg));
}
.vdb-collage-sequence.is-visible .two-aud-fan-card {
  animation: collageFanCardIn 620ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: calc(var(--reveal-delay, 0ms) + var(--collage-card-delay, 0ms));
}
.vdb-collage-sequence.is-visible .dark-band-thumb {
  animation: collageDarkThumbIn 620ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: calc(var(--reveal-delay, 0ms) + var(--collage-card-delay, 0ms));
}
.vdb-collage-sequence.is-visible .home-hook-float {
  animation: collageHookCardIn 620ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: calc(var(--reveal-delay, 0ms) + var(--collage-card-delay, 0ms));
}
@keyframes collageFanCardIn {
  to {
    opacity: 1;
    transform: var(--fan-card-transform, rotate(0deg));
  }
}
@keyframes collageDarkThumbIn {
  to {
    opacity: 1;
    transform: var(--dark-thumb-transform, rotate(0deg));
  }
}
@keyframes collageHookCardIn {
  to {
    opacity: 1;
    transform: var(--hook-card-transform, rotate(0deg));
  }
}
.hero-collage-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.5));
  pointer-events: none;
}
.hero-collage-outlier {
  position: absolute; top: 6px; right: 6px;
  background: rgba(255,255,255,0.92);
  border-radius: 100px;
  padding: 2px 7px;
  font-family: var(--font-display-italic);
  font-style: italic;
  font-weight: 600;
  font-size: 11px;
  color: var(--ink-800);
}
.hero-glass-quote {
  position: absolute;
  bottom: -20px; right: -10px;
  padding: 14px 18px;
  border-radius: 18px;
  max-width: 240px;
  transform: rotate(2deg);
  z-index: 20;
  background: var(--glass-bg);
  border: 2px solid var(--glass-border);
  backdrop-filter: blur(50px);
  -webkit-backdrop-filter: blur(50px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.16);
}
.concept-tile {
  display: block;
  padding: 16px 16px;
  background: var(--paper);
  border: 1px solid var(--line-200);
  border-radius: var(--vdb-card-radius);
  text-decoration: none;
  color: inherit;
  position: relative;
  z-index: 1;
  transition: box-shadow .12s ease, transform .12s ease;
}
.concept-tile:hover { transform: translateY(-1px); box-shadow: var(--shadow-card-sm); }
.concept-tile-name { font-size: 16px; font-weight: 600; color: var(--ink-800); margin-bottom: 4px; }
.concept-tile-count { font-size: 11px; color: var(--ink-400); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; }
.concept-tile-count em { font-family: var(--font-display-italic); font-style: italic; color: var(--ink-700); text-transform: none; letter-spacing: 0; font-size: 14px; }
.dark-band-thumb {
  position: relative;
  aspect-ratio: 9/13;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  border: 3px solid rgba(255,255,255,0.85);
  background: var(--ink-200);
  transform: var(--dark-thumb-transform, rotate(0deg));
}
.dark-band-thumb img { width: 100%; height: 100%; object-fit: cover; }
.dark-band-views {
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
}
.dark-band-badge-float {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-6deg);
  z-index: 10;
  filter: drop-shadow(0 4px 16px rgba(0,0,0,0.5));
}
.dark-band-thumbs {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, auto);
  gap: 10px;
  align-items: center;
}
.bangers-scroller-wrap {
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%);
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%);
}
.bangers-scroller {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 10px;
}
.bangers-scroller::-webkit-scrollbar { display: none; }
.bangers-scroller:active { cursor: grabbing; }
.bangers-scroller-item {
  flex: 0 0 220px;
  opacity: 0;
  transform: translateY(12px) scale(.985);
}
.bangers-scroller-wrap.is-visible .bangers-scroller-item {
  animation: bangerCardIn 520ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: var(--banger-card-delay, 0ms);
}
@keyframes bangerCardIn {
  to {
    opacity: 1;
    transform: none;
  }
}
.bangers-scroller-item .vdb-vcard {
  width: 100%;
}
.two-aud-fan-container:hover .two-aud-fan-card {
  transform: translateY(-4px) rotate(0deg) scale(1.025) !important;
}
.two-aud-fan-container:hover .two-aud-fan-card:nth-child(1) {
  transform: translate(-8px, 0) rotate(-8deg) scale(1.025) !important;
}
.two-aud-fan-container:hover .two-aud-fan-card:nth-child(3) {
  transform: translate(8px, -6px) rotate(8deg) scale(1.025) !important;
}
.dark-band-thumbs:hover .dark-band-thumb {
  transform: translateY(-4px) scale(1.025) !important;
}
/* Two audiences section */
.two-aud-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 48px;
}
.two-aud-header-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding-top: 40px;
}
.two-aud-grid {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 56px;
}
.two-aud-divider {
  border-left: 1px dashed var(--cream-500);
}
.two-aud-fan-container {
  position: relative;
  height: 340px;
  margin-bottom: 24px;
}
.two-aud-fan-card {
  width: 200px;
  aspect-ratio: 9 / 13;
  border: 3px solid var(--paper);
  border-radius: 12px;
  box-shadow: var(--shadow-card-md);
  overflow: hidden;
  background: var(--cream-300);
  transform: var(--fan-card-transform, rotate(0deg));
}
.two-aud-fan-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.two-aud-fan-skel {
  background: linear-gradient(135deg, var(--cream-300) 0%, var(--cream-200) 100%);
}
@media (max-width: 880px) {
  .two-aud-grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .two-aud-divider {
    border-left: none;
    border-top: 1px dashed var(--cream-500);
    margin: 40px 0;
  }
  .two-aud-fan-container {
    height: 300px;
  }
  .two-aud-fan-card {
    width: 170px;
  }
}
@media (max-width: 560px) {
  .two-aud-header {
    flex-direction: column;
  }
  .two-aud-header-meta {
    padding-top: 0;
  }
  .two-aud-fan-container {
    height: 260px;
  }
  .two-aud-fan-card {
    width: 140px;
  }
}
.home-format-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}
.home-format-grid .vdb-tile-title { font-size: 20px; }
.home-format-grid .vdb-tile-meta { font-size: 13px; }
.home-format-grid .vdb-tile-body { justify-content: center; }
@media (max-width: 768px) {
  .home-editorial { padding-top: 44px; padding-bottom: 44px; }
  .home-editorial-feature { min-height: 590px; }
  .home-editorial-feature > img { object-position: 58% center; }
  .home-editorial-overlay { background: linear-gradient(180deg, rgba(250,247,241,.94) 0%, rgba(250,247,241,.87) 52%, rgba(250,247,241,.4) 76%, rgba(16,24,29,.38) 100%); }
  .home-editorial-content { min-height: 590px; width: 100%; padding: 32px 28px 26px; }
  .home-editorial-meta { margin-bottom: 22px; }
  .home-editorial-feature h2 { max-width: 520px; font-size: clamp(38px, 10vw, 54px); }
  .home-editorial-dek { max-width: 520px; font-size: 16px; -webkit-line-clamp: 4; }
  .home-editorial-footer { color: white; text-shadow: 0 1px 12px rgba(0,0,0,.28); }
  .home-format-grid { grid-template-columns: 1fr; }
  .hero-section { padding-top: 32px; padding-bottom: 24px; }
  .hero-cta-row { margin-bottom: 16px; }
  .hero-grid { grid-template-columns: 1fr; gap: 24px; }
  .hero-grid > * { min-width: 0; }
  .hero-collage { transform: scale(0.7); transform-origin: top left; width: 143%; height: 460px; margin-bottom: -118px; }
  .hero-collage-card { border-width: 3px; }
  .hero-glass-quote { display: none; }
  .home-mcp-section { padding-top: 34px; padding-bottom: 34px; }
  .home-mcp-inner { grid-template-columns: 1fr; gap: 28px; }
  .home-mcp-demo { transform: none; }
  .home-mcp-tools > div { grid-template-columns: 22px 1fr; }
  .home-mcp-tools small { display: none; }
  .hero-stats { flex-wrap: wrap; gap: 24px; }
  .dark-band-thumbs { grid-template-columns: repeat(3, 1fr); margin-top: 28px; }
  .vdb-dark-band > div { grid-template-columns: 1fr !important; }
  .bangers-scroller-wrap {
    margin-inline: calc(var(--vdb-pad-x) * -1);
    padding-inline: var(--vdb-pad-x);
    mask-image: none;
    -webkit-mask-image: none;
  }
  .bangers-scroller {
    overflow-x: scroll;
    touch-action: pan-x;
    overscroll-behavior-x: contain;
    scroll-padding-inline: var(--vdb-pad-x);
  }
  .bangers-scroller-item { flex-basis: 170px; }
}
.page-graph-paper::after {
  -webkit-mask-image: linear-gradient(180deg, transparent 0, black 80px, black 450px, transparent 700px) !important;
          mask-image: linear-gradient(180deg, transparent 0, black 80px, black 450px, transparent 700px) !important;
}
.home-hooks-section {
  padding: 56px var(--vdb-pad-x);
  position: relative;
  isolation: isolate;
  background-color: rgb(243, 236, 232);
}
.home-hooks-section::after {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(43,40,39,0.16) 1.1px, transparent 1.4px);
  background-size: 22px 22px;
  pointer-events: none;
  z-index: 0;
}
.home-hooks-section > * { position: relative; z-index: 1; }
.home-hooks-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.home-hooks-graphic {
  position: relative;
  height: 340px;
}
.home-hooks-stack {
  position: relative;
  width: 100%;
  height: 100%;
}
.home-hook-float {
  position: absolute;
  background: var(--paper);
  border-radius: 16px;
  box-shadow: var(--shadow-card-md);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 320px;
  transition: transform 220ms cubic-bezier(.2, .7, .2, 1), box-shadow 220ms cubic-bezier(.2, .7, .2, 1);
  transform: var(--hook-card-transform, rotate(0deg));
}
.home-hooks-stack:hover .home-hook-float {
  box-shadow: 0 14px 34px rgba(0,0,0,0.14);
}
.home-hooks-stack:hover .home-hook-float-1 { transform: translate(-14px, -12px) rotate(-6deg) !important; }
.home-hooks-stack:hover .home-hook-float-2 { transform: translate(18px, -2px) rotate(4deg) !important; }
.home-hooks-stack:hover .home-hook-float-3 { transform: translate(-18px, 12px) rotate(-3deg) !important; }
.home-hook-float-1 { top: 0; left: 20px; --hook-card-transform: rotate(-4deg); --collage-card-delay: 0ms; z-index: 3; }
.home-hook-float-2 { top: 100px; left: 60px; --hook-card-transform: rotate(2deg); --collage-card-delay: 120ms; z-index: 2; }
.home-hook-float-3 { top: 200px; left: 10px; --hook-card-transform: rotate(-1.5deg); --collage-card-delay: 240ms; z-index: 1; }
.home-hooks-stack.vdb-collage-sequence .home-hook-float {
  opacity: 0;
  transform: translateY(22px) scale(.94) var(--hook-card-transform, rotate(0deg));
}
.home-hooks-stack.vdb-collage-sequence.is-visible .home-hook-float {
  animation: collageHookCardIn 720ms cubic-bezier(.2, .8, .2, 1) forwards;
  animation-delay: calc(var(--reveal-delay, 0ms) + var(--collage-card-delay, 0ms));
}
.home-hook-pills { display: flex; gap: 5px; }
.hf-pill {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 100px;
}
.home-hook-text {
  font-family: var(--font-display-italic);
  font-style: italic;
  font-size: 17px;
  line-height: 1.45;
  color: var(--ink-700);
  margin: 0;
}
.hf-var {
  background: #E8F4DB;
  color: #2d6b22;
  padding: 2px 8px;
  border-radius: 4px;
  font-style: normal;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
}
.home-hooks-cta-desktop,
.home-hooks-cta-mobile {
  width: fit-content;
  justify-self: start;
  align-self: flex-start;
}
.home-hooks-cta-mobile { display: none; }
@media (max-width: 768px) {
  .home-hooks-layout { grid-template-columns: 1fr; gap: 32px; }
  .home-hooks-graphic { height: 320px; }
  .home-hook-float { width: 280px; padding: 14px 16px; }
  .home-hook-float-1 { left: 5px; }
  .home-hook-float-2 { top: 90px; left: 30px; }
  .home-hook-float-3 { top: 180px; left: 0; }
  .home-hooks-cta-desktop { display: none; }
  .home-hooks-cta-mobile { display: inline-flex; margin-top: 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-collage,
  .hero-collage:hover,
  .hero-skel,
  .hero-collage-card,
  .hero-collage-card-frame,
  .hero-collage-card:hover,
  .hero-collage:hover .hero-collage-card,
  .hero-collage:hover .hero-collage-card:hover,
  .hero-collage:hover .hero-collage-card-frame,
  .hero-collage-card:hover .hero-collage-card-frame,
  .hero-collage:hover .hero-collage-card:hover .hero-collage-card-frame,
  .hero-collage:hover .hero-skel,
  .bangers-scroller-wrap.is-visible .bangers-scroller-item,
  .home-hook-float,
  .home-hooks-stack:hover .home-hook-float,
  .two-aud-fan-container:hover .two-aud-fan-card,
  .vdb-collage-sequence.is-visible .two-aud-fan-card,
  .vdb-collage-sequence.is-visible .dark-band-thumb,
  .vdb-collage-sequence.is-visible .home-hook-float,
  .dark-band-thumbs:hover .dark-band-thumb {
    animation: none !important;
    opacity: 1 !important;
    transition: none !important;
  }
  .hero-collage-card,
  .hero-skel,
  .hero-collage-card:hover,
  .hero-collage:hover .hero-collage-card,
  .hero-collage:hover .hero-collage-card:hover {
    transform: var(--home-card-transform) !important;
  }
  .hero-collage-card-frame,
  .hero-collage:hover .hero-collage-card-frame,
  .hero-collage-card:hover .hero-collage-card-frame,
  .hero-collage:hover .hero-collage-card:hover .hero-collage-card-frame {
    transform: none !important;
  }
  .bangers-scroller-item {
    transform: none !important;
  }
  .hero-skel {
    opacity: 0.26 !important;
  }
  .vdb-collage-sequence .two-aud-fan-card,
  .vdb-collage-sequence.is-visible .two-aud-fan-card {
    transform: var(--fan-card-transform, rotate(0deg)) !important;
  }
  .vdb-collage-sequence .dark-band-thumb,
  .vdb-collage-sequence.is-visible .dark-band-thumb {
    transform: var(--dark-thumb-transform, rotate(0deg)) !important;
  }
  .vdb-collage-sequence .home-hook-float,
  .vdb-collage-sequence.is-visible .home-hook-float {
    transform: var(--hook-card-transform, rotate(0deg)) !important;
  }
}
</style>
